import {
  CRow,
  CCol,
  CInput,
  CLabel,
  CFormGroup,
  CInputGroup,
  CInputGroupAppend,
  CButton,
  CCard,
  CCardHeader,
  CCardBody,
  CCardTitle,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import React, { useCallback, useState } from 'react';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import _t from 'counterpart';
import { useGetSetting } from './hooks';
import { useMutation } from 'react-query';
import { findErrorFromValidation, handleFormError } from '../../helpers';
import { updateTwilio } from '../../services/BackendService';
import toast from 'react-hot-toast';
import QueryDataRenderer from '../../components/QueryDataRenderer';

const TwilioIntegration = () => {
  const [accountSid, setAccountSid] = useState<string>('');
  const [messagingSid, setMessagingSid] = useState<string>('');
  const [authToken, setAuthToken] = useState<string>('');
  const [originalValues, setOriginalValues] = useState<{ accountSid: string; messagingSid: string; authToken: string }>({
    accountSid: '',
    messagingSid: '',
    authToken: '',
  });

  const accountSidQuery = useGetSetting('TWILIO_ACCOUNT_SID', 'twilio-account-sid', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      const val = String(value ?? '').trim();
      setAccountSid(val);
      setOriginalValues((prev) => ({ ...prev, accountSid: val }));
    },
  });

  const messagingSidQuery = useGetSetting('TWILIO_MESSAGING_SID', 'twilio-messaging-sid', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      const val = String(value ?? '').trim();
      setMessagingSid(val);
      setOriginalValues((prev) => ({ ...prev, messagingSid: val }));
    },
  });

  const authTokenQuery = useGetSetting('TWILIO_AUTHTOKEN', 'twilio-auth-token', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      const val = String(value ?? '').trim();
      setAuthToken(val);
      setOriginalValues((prev) => ({ ...prev, authToken: val }));
    },
  });

  const updateTwilioMutation = useMutation(
    ({ accountSid, messagingSid, authToken }: { accountSid: string; messagingSid: string; authToken: string }) =>
      updateTwilio(accountSid, messagingSid, authToken),
    {
      onError: (error: any) => {
        handleFormError(error);
      },
      onSuccess: () => {
        toast.success(_t('integrations.twilio-configured'));
        setOriginalValues({ accountSid, messagingSid, authToken });
        accountSidQuery.refetch();
        messagingSidQuery.refetch();
        authTokenQuery.refetch();
      },
    }
  );

  const handleSave = () => {
    updateTwilioMutation.mutate({
      accountSid: accountSid.trim(),
      messagingSid: messagingSid.trim(),
      authToken: authToken.trim(),
    });
  };

  const hasChanges =
    accountSid.trim() !== originalValues.accountSid ||
    messagingSid.trim() !== originalValues.messagingSid ||
    authToken.trim() !== originalValues.authToken;

  const hasAccountSid = accountSid.trim() !== '';
  const hasAuthToken = authToken.trim() !== '';
  const hasPartialFill = hasAccountSid !== hasAuthToken;

  const isLoading =
    accountSidQuery.isFetching || messagingSidQuery.isFetching || authTokenQuery.isFetching || updateTwilioMutation.isLoading;
  const isError =
    Boolean(accountSidQuery.error || messagingSidQuery.error || authTokenQuery.error) &&
    !accountSidQuery.isFetching &&
    !messagingSidQuery.isFetching &&
    !authTokenQuery.isFetching;

  const findError = (paramName: string) => {
    return findErrorFromValidation(updateTwilioMutation.error, paramName);
  };

  const copyToClipboard = useCallback((value: string) => {
    navigator.clipboard.writeText(value);
    toast.success(_t('global.copied'));
  }, []);

  const isTwilioConfigured = accountSid.trim() !== '' && authToken.trim() !== '';
  const apiRoot = process.env.REACT_APP_API_ROOT || '';
  const inboundWebhookUrl = apiRoot ? `${apiRoot.replace(/\/$/, '')}/api/twilio/inbound` : '';
  const statusWebhookUrl = apiRoot ? `${apiRoot.replace(/\/$/, '')}/api/twilio/status` : '';

  return (
    <CCol md={12}>
      <CCard>
        <CCardHeader>
          <CCardTitle>{_t('integrations.twilio-integration')}</CCardTitle>
          <p className="mb-0 mt-1 small">{_t('integrations.twilio-integration-description')}</p>
        </CCardHeader>
        <CCardBody>
          <QueryDataRenderer
            isError={isError}
            isLoading={isLoading}
            onRefetch={() => {
              accountSidQuery.refetch();
              messagingSidQuery.refetch();
              authTokenQuery.refetch();
            }}
          >
            <CRow>
              <CCol md={12}>
                <CFormGroup>
                  <CLabel htmlFor="twilio-account-sid">{_t('integrations.twilio-account-sid')}</CLabel>
                  <CInputGroup>
                    <CInput
                      type="text"
                      id="twilio-account-sid"
                      name="accountSid"
                      value={accountSid}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccountSid(e.target.value)}
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      className="instrument-input-height"
                    />
                  </CInputGroup>
                  {findError('accountSid') && <CLabel className="text-danger">{findError('accountSid')}</CLabel>}
                </CFormGroup>
              </CCol>
              <CCol md={12}>
                <CFormGroup>
                  <CLabel htmlFor="twilio-messaging-sid">{_t('integrations.twilio-messaging-sid')}</CLabel>
                  <CInputGroup>
                    <CInput
                      type="text"
                      id="twilio-messaging-sid"
                      name="messagingSid"
                      value={messagingSid}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMessagingSid(e.target.value)}
                      placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      className="instrument-input-height"
                    />
                  </CInputGroup>
                  {findError('messagingSid') && <CLabel className="text-danger">{findError('messagingSid')}</CLabel>}
                </CFormGroup>
              </CCol>
              <CCol md={12}>
                <CFormGroup>
                  <CLabel htmlFor="twilio-auth-token">{_t('integrations.twilio-auth-token')}</CLabel>
                  <CInputGroup>
                    <CInput
                      type="password"
                      id="twilio-auth-token"
                      name="authToken"
                      value={authToken}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAuthToken(e.target.value)}
                      placeholder="••••••••••••••••"
                      className="instrument-input-height"
                    />
                  </CInputGroup>
                  {findError('authToken') && <CLabel className="text-danger">{findError('authToken')}</CLabel>}
                </CFormGroup>
              </CCol>
              <CCol md={12} className="d-flex justify-content-end">
                <ButtonWithLoader
                  isLoading={updateTwilioMutation.isLoading}
                  onClick={handleSave}
                  buttonColor="primary"
                  spinnerColor="secondary"
                  title={_t('action.save')}
                  className="btn-md"
                  showText={false}
                  disabled={!hasChanges || hasPartialFill}
                />
              </CCol>
              {isTwilioConfigured && inboundWebhookUrl && (
                <>
                  <CCol md={12}>
                    <CFormGroup>
                      <CLabel>{_t('integrations.twilio-webhook-inbound')}</CLabel>
                      <p className="mb-2 mt-1 small">{_t('integrations.twilio-webhook-inbound-description')}</p>
                      <CInputGroup>
                        <CInput
                          type="text"
                          value={inboundWebhookUrl}
                          readOnly
                          className="instrument-input-height bg-light"
                        />
                        <CInputGroupAppend>
                          <CButton
                            color="secondary"
                            onClick={() => copyToClipboard(inboundWebhookUrl)}
                            title={_t('global.copy')}
                          >
                            <CIcon name="cil-copy" />
                          </CButton>
                        </CInputGroupAppend>
                      </CInputGroup>
                    </CFormGroup>
                  </CCol>
                  <CCol md={12}>
                    <CFormGroup>
                      <CLabel>{_t('integrations.twilio-webhook-status')}</CLabel>
                      <p className="mb-2 mt-1 small">{_t('integrations.twilio-webhook-status-description')}</p>
                      <CInputGroup>
                        <CInput
                          type="text"
                          value={statusWebhookUrl}
                          readOnly
                          className="instrument-input-height bg-light"
                        />
                        <CInputGroupAppend>
                          <CButton
                            color="secondary"
                            onClick={() => copyToClipboard(statusWebhookUrl)}
                            title={_t('global.copy')}
                          >
                            <CIcon name="cil-copy" />
                          </CButton>
                        </CInputGroupAppend>
                      </CInputGroup>
                    </CFormGroup>
                  </CCol>
                </>
              )}
            </CRow>
          </QueryDataRenderer>
        </CCardBody>
      </CCard>
    </CCol>
  );
};

export default TwilioIntegration;
